/**
 * Client for the feat-039 POS-matching-layer admin endpoints.
 *
 * Contracts:
 *  - Spec:  worktrees/feat-039-pos-matching-layer/spec.md (F1–F3)
 *  - ADR 0005 (POS matching layer) — D2 (match queue + order match state),
 *    D1 (server_pos_mapping), D5 (coverage), D6 (TIMECARDS_READ re-consent / B6a).
 *
 * This file is the single source of truth for the frontend-facing contract.
 * The feat-039 backend implements its endpoints to match these shapes (see the
 * contract-alignment message sent to the backend + architect peers). Where the
 * ADR pins a shape (coverage D5, server_pos_mapping D1) we mirror it verbatim.
 *
 * Fake mode: when `config.environment === 'fake'` (npm run start:fake), every
 * call returns canned data so the F1/F2/F3 surfaces render without a backend —
 * mirroring the squareConnectionService.ts precedent.
 *
 * DECIMAL columns (price, *_seconds, overlap_minutes) arrive as strings from
 * MySQL and are coerced to numbers at the service edge per
 * interviewer-frontend/CLAUDE.md.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

// ---------- Shared ----------

/** POS vendor discriminator. v1 only emits 'square'; the schema is source-agnostic. */
export type PosSource = 'square' | 'toast' | 'clover' | 'revel';

const isFake = (): boolean => config.environment === 'fake';

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
};

async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const init: RequestInit = { method: options.method ?? 'GET', headers };
  if (options.body !== undefined) {
    init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
  }
  const resp = await authedApiFetch(backendUrl(path), init);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  if (resp.status === 204) return undefined as unknown as T;
  return resp.json();
}

const toNum = (v: unknown): number => (typeof v === 'string' ? parseFloat(v) : Number(v ?? 0));
const toNumOrNull = (v: unknown): number | null =>
  v === null || v === undefined ? null : toNum(v);

// =====================================================================
// F1 — Order match state (D2: rematch/unmatch/inspect + queue stats)
// =====================================================================

/** A POS line item linked to an AI-detected shift_interaction (upsell parity, D4). */
export interface PosMatchedItem {
  pos_order_line_item_id: string;
  item_name: string;
  quantity: number;
  price: number | null;
  shift_interaction_id: string;
  /** metric_1 | metric_2 | metric_3 | drink_promotion (validated server-side against tenant metrics). */
  match_rule: string;
  time_diff_seconds: number;
  /** AI explanation for the attribution; null falls back to a generic line in the UI. */
  explanation: string | null;
}

/**
 * Current match state for one order, keyed on (source, vendor_order_id).
 * `matched` is true iff a pos_order_shift_link row exists for the latest snapshot.
 */
export interface OrderMatchState {
  source: PosSource;
  vendor_order_id: string;
  matched: boolean;
  shift_id: string | null;
  /** Seconds from shift start — same basis as transcript_part.start_time. */
  shift_offset_seconds: number | null;
  transcript_part_id: string | null;
  matched_at: string | null; // ISO-8601 UTC
  matched_items: PosMatchedItem[];
}

export interface MatchQueueStats {
  depth: number;
  in_flight: number;
  dlq_depth: number;
}

function fakeMatchState(source: PosSource, vendorOrderId: string): OrderMatchState {
  // f-3 (Dubai Chocolate) is intentionally returned UNMATCHED so QA can see the
  // no-match branch + drive Rematch; everything else returns a matched example.
  if (vendorOrderId.endsWith('f-3') || vendorOrderId.endsWith('unmatched')) {
    return {
      source,
      vendor_order_id: vendorOrderId,
      matched: false,
      shift_id: null,
      shift_offset_seconds: null,
      transcript_part_id: null,
      matched_at: null,
      matched_items: [],
    };
  }
  return {
    source,
    vendor_order_id: vendorOrderId,
    matched: true,
    shift_id: 'fake-shift-1',
    shift_offset_seconds: 742.5,
    transcript_part_id: 'fake-tp-88',
    matched_at: new Date(Date.now() - 6 * 60_000).toISOString(),
    matched_items: [
      {
        pos_order_line_item_id: 'fake-li-1',
        item_name: 'Steak Shawarma',
        quantity: 1,
        price: 18.5,
        shift_interaction_id: 'fake-int-1',
        match_rule: 'metric_1',
        time_diff_seconds: 12.4,
        explanation:
          'Server offered the steak shawarma upgrade right before the order closed; customer accepted.',
      },
      {
        pos_order_line_item_id: 'fake-li-2',
        item_name: 'Sparkling Water',
        quantity: 1,
        price: 4.0,
        shift_interaction_id: 'fake-int-2',
        match_rule: 'drink_promotion',
        time_diff_seconds: -33.1,
        explanation: 'Drink promotion mentioned ~30s before the item was rung up.',
      },
    ],
  };
}

/**
 * Order-match endpoints nest under /locations/{loc}/ (ADR 0005 §D7): the
 * location segment carries the tenant scope so the backend can fail closed
 * (cross-tenant/cross-location id → 404) without trusting a flat path. The F1
 * panel already extends the per-location Square transactions panel, so the
 * Bespoke location id is always in hand at the call site.
 */
function orderPath(locationId: string, source: PosSource, vendorOrderId: string): string {
  return `/api/admin/locations/${encodeURIComponent(locationId)}/pos/orders/${encodeURIComponent(source)}/${encodeURIComponent(vendorOrderId)}`;
}

export async function getOrderMatchState(
  bespokeLocationId: string,
  source: PosSource,
  vendorOrderId: string,
): Promise<OrderMatchState> {
  if (isFake()) return fakeMatchState(source, vendorOrderId);
  const raw = await api<Record<string, unknown>>(`${orderPath(bespokeLocationId, source, vendorOrderId)}/match`);
  const rawItems = Array.isArray(raw.matched_items)
    ? (raw.matched_items as Array<Record<string, unknown>>)
    : [];
  return {
    source: (raw.source as PosSource) ?? source,
    vendor_order_id: String(raw.vendor_order_id ?? vendorOrderId),
    matched: Boolean(raw.matched),
    shift_id: (raw.shift_id as string | null) ?? null,
    shift_offset_seconds: toNumOrNull(raw.shift_offset_seconds),
    transcript_part_id: (raw.transcript_part_id as string | null) ?? null,
    matched_at: (raw.matched_at as string | null) ?? null,
    matched_items: rawItems.map((it) => ({
      pos_order_line_item_id: String(it.pos_order_line_item_id ?? ''),
      item_name: String(it.item_name ?? ''),
      quantity: toNum(it.quantity),
      price: toNumOrNull(it.price),
      shift_interaction_id: String(it.shift_interaction_id ?? ''),
      match_rule: String(it.match_rule ?? ''),
      time_diff_seconds: toNum(it.time_diff_seconds),
      explanation: (it.explanation as string | null) ?? null,
    })),
  };
}

/** Enqueue a rematch (async, D2). Returns immediately; backend responds 202. */
export async function rematchOrder(
  bespokeLocationId: string,
  source: PosSource,
  vendorOrderId: string,
): Promise<void> {
  if (isFake()) return;
  await api<void>(`${orderPath(bespokeLocationId, source, vendorOrderId)}/rematch`, { method: 'POST' });
}

/** Clear link rows synchronously (no re-enqueue) — testing/fix aid. */
export async function unmatchOrder(
  bespokeLocationId: string,
  source: PosSource,
  vendorOrderId: string,
): Promise<void> {
  if (isFake()) return;
  await api<void>(`${orderPath(bespokeLocationId, source, vendorOrderId)}/match`, { method: 'DELETE' });
}

export async function getMatchQueueStats(): Promise<MatchQueueStats> {
  if (isFake()) return { depth: 3, in_flight: 1, dlq_depth: 0 };
  const raw = await api<Record<string, unknown>>('/api/admin/pos/match-queue/stats');
  return {
    depth: toNum(raw.depth),
    in_flight: toNum(raw.in_flight),
    dlq_depth: toNum(raw.dlq_depth),
  };
}

// =====================================================================
// F2 — Server ↔ Square Team Member mapping (D1: server_pos_mapping)
// =====================================================================

export interface PosEmployee {
  source: PosSource;
  /** Stable vendor employee id (Square Team Member id). */
  vendor_employee_id: string;
  display_name: string;
  vendor_location_id: string | null;
  /** null = unmapped. */
  mapped_internal_server_id: string | null;
  mapped_server_name: string | null;
}

export interface PosServerMapping {
  source: PosSource;
  vendor_employee_id: string;
  internal_server_id: string;
}

const FAKE_EMPLOYEES: PosEmployee[] = [
  {
    source: 'square',
    vendor_employee_id: 'sq-emp-1',
    display_name: 'Jane Kowalski',
    vendor_location_id: 'sq-loc-1',
    mapped_internal_server_id: 'srv-1',
    mapped_server_name: 'Jane K.',
  },
  {
    source: 'square',
    vendor_employee_id: 'sq-emp-2',
    display_name: 'Alex Park',
    vendor_location_id: 'sq-loc-1',
    mapped_internal_server_id: null,
    mapped_server_name: null,
  },
  {
    source: 'square',
    vendor_employee_id: 'sq-emp-3',
    display_name: 'Maria Santos',
    vendor_location_id: 'sq-loc-1',
    mapped_internal_server_id: null,
    mapped_server_name: null,
  },
];

export async function listPosEmployees(
  tenantId: string,
  source: PosSource = 'square',
): Promise<PosEmployee[]> {
  if (isFake()) return FAKE_EMPLOYEES.filter((e) => e.source === source);
  const raw = await api<{ employees: Array<Record<string, unknown>> }>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/pos/employees?source=${encodeURIComponent(source)}`,
  );
  return (raw.employees ?? []).map((e) => ({
    source: (e.source as PosSource) ?? source,
    vendor_employee_id: String(e.vendor_employee_id ?? ''),
    display_name: String(e.display_name ?? ''),
    vendor_location_id: (e.vendor_location_id as string | null) ?? null,
    mapped_internal_server_id: (e.mapped_internal_server_id as string | null) ?? null,
    mapped_server_name: (e.mapped_server_name as string | null) ?? null,
  }));
}

/** Upsert a mapping on the natural key (source, vendor_employee_id). */
export async function setServerMapping(
  tenantId: string,
  mapping: PosServerMapping,
): Promise<void> {
  if (isFake()) return;
  await api<void>(`/api/admin/tenants/${encodeURIComponent(tenantId)}/pos/server-mappings`, {
    method: 'PUT',
    body: mapping,
  });
}

export async function removeServerMapping(
  tenantId: string,
  source: PosSource,
  vendorEmployeeId: string,
): Promise<void> {
  if (isFake()) return;
  await api<void>(
    `/api/admin/tenants/${encodeURIComponent(tenantId)}/pos/server-mappings/${encodeURIComponent(source)}/${encodeURIComponent(vendorEmployeeId)}`,
    { method: 'DELETE' },
  );
}

// =====================================================================
// F3 — Coverage report (D5: clocked-in vs recording, 3 buckets)
// =====================================================================

/** Per-employee coverage row. Shape mirrors ADR 0005 D5 verbatim. */
export interface CoverageRow {
  vendor_employee_id: string;
  display_name: string;
  internal_server_id: string | null;
  mapped: boolean;
  square_clocked_in: boolean;
  bespoke_recording: boolean;
  overlap_minutes: number;
  /** ok = clocked-in AND recording; gap = clocked-in, mapped, not recording; unmapped = clocked-in, no mapping. */
  state: 'ok' | 'gap' | 'unmapped';
}

export interface CoverageReport {
  location_id: string;
  window: { start: string; end: string };
  /**
   * False when the merchant has not granted TIMECARDS_READ (B6a). The roster
   * still renders; clock-in columns show "needs re-authorization" rather than
   * an error. Backend sets this per ADR 0005 D6 graceful-degradation.
   */
  labor_authorized: boolean;
  rows: CoverageRow[];
}

function fakeCoverage(locationId: string, date: string): CoverageReport {
  const start = `${date}T16:00:00Z`;
  const end = `${date}T23:59:00Z`;
  return {
    location_id: locationId,
    window: { start, end },
    labor_authorized: true,
    rows: [
      {
        vendor_employee_id: 'sq-emp-1',
        display_name: 'Jane Kowalski',
        internal_server_id: 'srv-1',
        mapped: true,
        square_clocked_in: true,
        bespoke_recording: true,
        overlap_minutes: 312,
        state: 'ok',
      },
      {
        vendor_employee_id: 'sq-emp-4',
        display_name: 'Devon Lee',
        internal_server_id: 'srv-4',
        mapped: true,
        square_clocked_in: true,
        bespoke_recording: false,
        overlap_minutes: 188,
        state: 'gap',
      },
      {
        vendor_employee_id: 'sq-emp-2',
        display_name: 'Alex Park',
        internal_server_id: null,
        mapped: false,
        square_clocked_in: true,
        bespoke_recording: false,
        overlap_minutes: 95,
        state: 'unmapped',
      },
    ],
  };
}

export async function getCoverageReport(
  locationId: string,
  date: string,
  at?: string,
): Promise<CoverageReport> {
  if (isFake()) return fakeCoverage(locationId, date);
  const qs = new URLSearchParams({ date });
  if (at) qs.set('at', at);
  const raw = await api<Record<string, unknown>>(
    `/api/admin/locations/${encodeURIComponent(locationId)}/coverage?${qs.toString()}`,
  );
  const rawRows = Array.isArray(raw.rows) ? (raw.rows as Array<Record<string, unknown>>) : [];
  const win = (raw.window ?? {}) as Record<string, unknown>;
  return {
    location_id: String(raw.location_id ?? locationId),
    window: { start: String(win.start ?? ''), end: String(win.end ?? '') },
    // Default true when omitted so older backends (pre-B6a) don't all show as degraded.
    labor_authorized: raw.labor_authorized === undefined ? true : Boolean(raw.labor_authorized),
    rows: rawRows.map((r) => ({
      vendor_employee_id: String(r.vendor_employee_id ?? ''),
      display_name: String(r.display_name ?? ''),
      internal_server_id: (r.internal_server_id as string | null) ?? null,
      mapped: Boolean(r.mapped),
      square_clocked_in: Boolean(r.square_clocked_in),
      bespoke_recording: Boolean(r.bespoke_recording),
      overlap_minutes: toNum(r.overlap_minutes),
      state: (r.state as CoverageRow['state']) ?? 'unmapped',
    })),
  };
}
